import { AppDispatch, RootState } from '../Store';
import { Api } from '../../Api/Api';
import { ISeedResponse } from '../../Models/Responses/SeedResponse';
import { ICompetitorWireObject, ICompetitorResponse, ICompetitorObject } from '../../Models/Responses/CompetitorResponse';
import { getCurrentActingUser } from '../User/AuthSlice';
import { createTracedAsyncThunk } from '../../../tracing/trace';
import { CropAndBrandAvailability7, HybridAvailability7, LocalPosition } from '../../Models/Responses/SeedResponseV7';

export interface ISeries
{
	availability: string;
	brandName: string;
	rank?: number;
	relativeMaturity: string;
	seriesId: string;
	seriesName: string;
}
export interface ISeriesWithHybrid extends ISeries
{
	hybrids: HybridAvailability7[];
}

export interface ISeedResponseWithGrowerZip
{
	seeds: ISeedResponse[];
	growerZip: string;
}

export const getSeedsForGrower = createTracedAsyncThunk<
	{
		seeds: CropAndBrandAvailability7[],
		growerZip: string,
		growerId: string,
	}, 
	undefined, 
	{ dispatch: AppDispatch, state: RootState }
>(
	'seeds/7/grower/get',
	async (context, request: undefined, thunkAPI) =>
	{
		const currentState = thunkAPI.getState();
		const currentUser = getCurrentActingUser(currentState);
		const grower = (currentState.ui.SelectedGrowerId) ? currentState.grower.Growers.find(g => g.Id === currentState.ui.SelectedGrowerId): undefined;
		const growerZip = grower?.Zip ?? '';
		
		const { auth, ui } = currentState;
		const url = `users/${currentUser.UserId}/growers/${ui.SelectedGrowerId}/seeds`;
		try
		{
			const api = new Api('/api/7', auth.userAuthToken, context);
			const response = await api.getAsync<CropAndBrandAvailability7[]>(url);

			if (response.ErrorCode === null && response.Success)
			{
				return { seeds: response.Data, growerZip: growerZip, growerId: grower?.Id };
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export interface IGetEnogenContractCheck
{
	cropYear: string;
	totalBags: string;
}
export const getEnogenContractCheck = createTracedAsyncThunk<boolean, IGetEnogenContractCheck, { dispatch: AppDispatch, state: RootState }>(
	'enogen/contractCheck',
	async (context, request, thunkAPI) =>
	{
		try
		{
			const { cropYear, totalBags } = request;
			const currentState = thunkAPI.getState();
			const growerId = currentState.ui.SelectedGrowerId;

			const api = new Api('/api/6', currentState.auth.userAuthToken, context);
			const response = await api.getAsync<boolean>(`growers/${growerId}/years/${cropYear}/enogenContracts/status?totalBags=${totalBags}`);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export const getCompetitorData = createTracedAsyncThunk<{ [key: string]: ICompetitorObject[] }, {requestedYear?: number}, { dispatch: AppDispatch, state: RootState }>(
	'competitors/get',
	async (context, request: {requestedYear?: number}, thunkAPI) =>
	{
		try
		{
			const currentState = thunkAPI.getState();
			const selectedYear = request.requestedYear ? request.requestedYear : currentState.crops.CropYear;

			const api = new Api('/api/6', currentState.auth.userAuthToken, context);
			const response = await api.getAsync<{ [key: string]: ICompetitorWireObject[] }>(`seeds/competitors?year=${selectedYear}`);

			if (response.ErrorCode === null && response.Success)
			{
				const competitorResponse: ICompetitorResponse = { Competitors: {} };
				competitorResponse.Competitors = response.Data;

				// Massage the returned data into the new object format
				const convertedCompetitorResponseData: { [key: string]: ICompetitorObject[] } = {};

				const allCompetitorWireData = Object.values(competitorResponse.Competitors).flatMap(c => c);
				allCompetitorWireData.forEach(c =>
				{
					if (!Object.keys(convertedCompetitorResponseData).includes(c.CropId))
					{
						// Add the crop ID and the first brand to the dictionary
						const convertedCompetitor: ICompetitorObject =
						{
							Id: c.CompetitorId,
							BrandName: c.CompetitorName,
							Products: [
								{
									Id: c.Id,
									ProductName: c.Name,
									RelativeMaturity: c.RelativeMaturity
								}
							],
							Year: c.Year
						};
						convertedCompetitorResponseData[c.CropId] = [convertedCompetitor];
					}
					else
					{
						const foundConvertedCompetitorIndex = convertedCompetitorResponseData[c.CropId].findIndex(cc => cc.Id === c.CompetitorId);
						if (foundConvertedCompetitorIndex < 0)
						{
							// Push the competitor brand and product onto the crop
							const convertedCompetitor: ICompetitorObject =
							{
								Id: c.CompetitorId,
								BrandName: c.CompetitorName,
								Products: [
									{
										Id: c.Id,
										ProductName: c.Name,
										RelativeMaturity: c.RelativeMaturity
									}
								],
								Year: c.Year
							};
							convertedCompetitorResponseData[c.CropId].push(convertedCompetitor);
						}
						else
						{
							// Push just the product onto the existing competitor brand
							convertedCompetitorResponseData[c.CropId][foundConvertedCompetitorIndex].Products.push(
								{
									Id: c.Id,
									ProductName: c.Name,
									RelativeMaturity: c.RelativeMaturity
								}
							);
						}
					}
				});

				return convertedCompetitorResponseData;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Gets all seeds for the LOGGED IN user; the backend determines the user from the auth token
 * Can take an optional year parameter to get seeds for a specific year
 */
export const getSeedsForUser = createTracedAsyncThunk<ISeedResponse[], {requestedYear?: number}, { dispatch: AppDispatch, state: RootState }>(
	'seeds/user/get',
	async (context, request: {requestedYear?: number}, thunkAPI) =>
	{
		const currentState = thunkAPI.getState();
		try
		{
			const api = new Api('/api/6', currentState.auth.userAuthToken, context);
			let url = '/seeds';
			if (request.requestedYear)
			{
				// If a year is requested, append it to the URL as a query parameter
				url += `?year=${request.requestedYear}`;
			}
			const response = await api.getAsync<ISeedResponse[]>(url);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Gets all seeds for a specific seller id (managed user/seller or the logged in user if no managed seller is currently selected)
 * Can take an optional year parameter to get seeds for a specific year
 */
export const getSeedsForSeller = createTracedAsyncThunk<ISeedResponse[], {requestedYear?: number}, { dispatch: AppDispatch, state: RootState }>(
	'seeds/seller/get',
	async (context, request: {requestedYear?: number}, thunkAPI) =>
	{
		const currentState = thunkAPI.getState();
		try
		{
			const api = new Api('/api/6', currentState.auth.userAuthToken, context);
			const currentUser = getCurrentActingUser(currentState);
			let url = `/seeds/users/${currentUser.UserId}`;
			if (request.requestedYear)
			{
				// If a year is requested, append it to the URL as a query parameter
				url += `?year=${request.requestedYear}`;
			}
			const response = await api.getAsync<ISeedResponse[]>(url);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Gets the local positioning for the provided year, listed by series Id
 */
export const getLocalPositioning = createTracedAsyncThunk<{[key: string]: LocalPosition[]}, {requestedYear?: number}, { dispatch: AppDispatch, state: RootState }>(
	'localpositioning/get',
	async (context, request: {requestedYear?: number}, thunkAPI) =>
	{
		const currentState = thunkAPI.getState();
		try
		{
			const api = new Api('/api/6', currentState.auth.userAuthToken, context);
			const growerId = currentState.ui.SelectedGrowerId;
			const url = `/growers/${growerId}/years/${request.requestedYear ?? currentState.crops.CropYear}/localPositioning?maxRegions=1`;
			const response = await api.getAsync<{[key: string]: LocalPosition[]}>(url);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

